import * as React from "react";
import { CalendarCheck, CalendarX2, Clock, MapPin, X } from "lucide-react";

import { apiErrorMessage, bookRollingLinkSlots, getPublicRollingLink } from "@/api/client";
import {
  PublicRollingLinkConfirmationView,
  PublicRollingLinkSlot,
  PublicRollingLinkView,
  SlotPagingBounds,
  SlotWindow,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { BookingCalendar, CalendarSlot } from "./BookingCalendar";
import { noteForRequest } from "./inviteeNote";
import { canGoNext, canGoPrev, initialMonth, useSlotPaging } from "./useSlotPaging";
import { useEmbedOptions } from "./public/embedMode";
import { BookingPageSkeleton, PublicLoadingLoop, useLoadingWindow } from "./public/PublicLoading";
import { Card, HeaderCard, PublicFooter, PublicPageShell } from "./public/PublicChrome";
import "./PublicRollingLinkPage.css";

/**
 * The public, no-login rolling-appointment page an invitee reaches at /@owner/{handle}. A
 * Microsoft-Bookings-inspired layout on the landing palette (blue primary, teal accent, coral
 * highlight): a banner header card, a tinted "what you're booking" card, then our own calendar with
 * a timezone picker. Its own component (a rolling appointment can book SEVERAL slots at once, so it
 * grows a selection tray). The blue primary comes from PublicRollingLinkPage.css, scoped to .inv-theme.
 */

type Load =
  | { status: "loading" }
  | { status: "error"; kind: "not-found" | "unreachable" }
  | { status: "ready"; rollingLink: PublicRollingLinkView };

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
}
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}


export function PublicRollingLinkPage({
  ownerHandle,
  rollingLinkHandle,
}: {
  ownerHandle: string;
  rollingLinkHandle: string;
}) {
  const embed = useEmbedOptions();
  const [load, setLoad] = React.useState<Load>({ status: "loading" });
  const [selected, setSelected] = React.useState<PublicRollingLinkSlot[]>([]);
  const [showForm, setShowForm] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState<PublicRollingLinkConfirmationView | null>(null);

  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [note, setNote] = React.useState("");
  const [website, setWebsite] = React.useState(""); // honeypot
  const [booking, setBooking] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fetchRollingLink = React.useCallback(() => {
    setLoad({ status: "loading" });
    getPublicRollingLink(ownerHandle, rollingLinkHandle)
      .then((rollingLink) => setLoad({ status: "ready", rollingLink }))
      .catch((e: unknown) => {
        const status = (e as { status?: number }).status;
        setLoad({ status: "error", kind: status === 404 ? "not-found" : "unreachable" });
      });
  }, [ownerHandle, rollingLinkHandle]);

  React.useEffect(() => {
    fetchRollingLink();
  }, [fetchRollingLink]);

  const selectedKeys = React.useMemo(
    () => new Set(selected.map((s) => new Date(s.start).getTime())),
    [selected],
  );

  // The first response carries the link's bounds and its first month of slots; every later month is
  // fetched on demand and kept, so paging back is instant. See useSlotPaging.
  const bounds: SlotPagingBounds = load.status === "ready" ? load.rollingLink : {};
  const fetchMonth = React.useCallback(
    (window: SlotWindow) =>
      getPublicRollingLink(ownerHandle, rollingLinkHandle, window).then((v) => v.slots),
    [ownerHandle, rollingLinkHandle],
  );
  const seed = React.useMemo(
    () =>
      load.status === "ready"
        ? { slots: load.rollingLink.slots, month: initialMonth(load.rollingLink) }
        : null,
    [load],
  );
  const paging = useSlotPaging<PublicRollingLinkSlot>(bounds, seed, fetchMonth);

  // Memoised: this array is a prop of the calendar, which groups it by day into a map the
  // auto-select effect depends on. A fresh array every render made that effect fire every render.
  const openSlots: CalendarSlot[] = React.useMemo(
    () => paging.slots.filter((s) => !selectedKeys.has(new Date(s.start).getTime())),
    [paging.slots, selectedKeys],
  );

  const pick = (slot: CalendarSlot): void => {
    setSelected([slot]);
    setShowForm(true);
  };

  const removeSelected = (start: string): void => {
    setSelected((prev) => prev.filter((s) => s.start !== start));
  };

  const submit = async (): Promise<void> => {
    if (load.status !== "ready" || !email.trim() || selected.length === 0) return;
    setBooking(true);
    setError(null);
    try {
      const result = await bookRollingLinkSlots(ownerHandle, rollingLinkHandle, {
        starts: selected.map((s) => s.start),
        name: name.trim() || undefined,
        email: email.trim(),
        note: noteForRequest(note),
        website: website || undefined,
      });
      setConfirmed(result);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBooking(false);
    }
  };

  const wide = load.status === "ready" && !confirmed && !showForm;

  // Held on screen for one full revolution once it appears, and not painted at all
  // if the fetch beats 120ms. The ready and error branches gate on it too: during
  // the hold the fetch has finished, so otherwise both would render at once.
  const showLoading = useLoadingWindow(load.status === "loading");

  return (
    <PublicPageShell embed={embed} width={wide ? "max-w-3xl" : "max-w-xl"}>
      {showLoading && (
        <Card className="p-6">
          {/* The loop on top, the grid below it. The colour is the status; the grey is
              the shape of what is coming. Never the other way round, and never
              overlaid: artwork ON the grid points at the middle of a card that is
              about to redraw completely. */}
          <PublicLoadingLoop className="pt-0 pb-8" />
          <BookingPageSkeleton />
        </Card>
      )}

      {!showLoading && load.status === "error" && (
        <Card className="p-8 text-center">
          <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40">
            <CalendarX2 className="h-6 w-6 text-amber-700 dark:text-amber-300" />
          </div>
          {load.kind === "not-found" ? (
            <>
              <h1 className="text-lg font-bold text-foreground">
                This appointment has ended or doesn't exist
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Ask whoever shared the link for a current one.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-lg font-bold text-foreground">We couldn't reach the server</h1>
              <p className="mt-1 text-sm text-muted-foreground">Give it a moment and try again.</p>
              <Button className="mt-4" onClick={fetchRollingLink}>
                Try again
              </Button>
            </>
          )}
        </Card>
      )}

      {!showLoading && load.status === "ready" && confirmed && (
        <>
          {/* hide_header lives at the call site, not inside HeaderCard: that component is shared by
              all six invitee pages, and reading the flag there would let the ad-hoc and poll pages
              honour it too. They are not embeddable, so they must not. */}
          {!embed.hideHeader && (
            <HeaderCard
              name={confirmed.host_name}
              subtitle="Schedule a meeting"
              avatarUrl={load.rollingLink.host_avatar_url}
              bannerUrl={load.rollingLink.host_banner_url}
              socialLinks={load.rollingLink.host_social_links}
            />
          )}
          <Card className="p-6 text-center">
            <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/40">
              <CalendarCheck className="h-6 w-6 text-green-700 dark:text-green-300" />
            </div>
            <h1 className="text-lg font-bold text-foreground">
              You're booked
              {confirmed.appointments.length > 1 ? ` (${confirmed.appointments.length} times)` : ""}!
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">with {confirmed.host_name}</p>
            <ul className="mt-4 space-y-2 text-left">
              {confirmed.appointments.map((a) => (
                <li key={a.start} className="rounded-xl bg-muted/50 px-3 py-2">
                  <p className="text-sm font-semibold text-foreground">{fmtDay(a.start)}</p>
                  <p className="text-sm text-muted-foreground">
                    {fmtTime(a.start)} – {fmtTime(a.end)}
                  </p>
                  {a.meeting_url && (
                    <p className="mt-1 text-sm text-[#2779a7] dark:text-[#7cc3e6]">
                      A {a.meeting_provider ? "video" : ""} link is in your email.
                    </p>
                  )}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-muted-foreground">
              A calendar invite is on its way to {confirmed.invitee_email}, with cancel and reschedule
              links inside.
            </p>
          </Card>
        </>
      )}

      {!showLoading && load.status === "ready" && !confirmed && (
        <>
          {!embed.hideHeader && (
            <HeaderCard
              name={load.rollingLink.host_name}
              subtitle="Schedule a meeting"
              avatarUrl={load.rollingLink.host_avatar_url}
              bannerUrl={load.rollingLink.host_banner_url}
              socialLinks={load.rollingLink.host_social_links}
            />
          )}

          <Card className="p-6 sm:p-7">
            {/* hide_details: an embedding site has almost always just said what the meeting is
                in its own words, directly above the widget, so repeating it is duplication in
                their layout. The slot picker below is never suppressible: it is the page. */}
            {!embed.hideDetails && (
              <>
                <h2 className="mb-3 text-sm font-bold text-foreground">What you're booking</h2>
                <div className="flex items-start gap-3 rounded-xl border border-[#bfe0ef] bg-[#e9f3f7] p-4 dark:border-[#2779a7]/50 dark:bg-[#1e5f83]/20">
                  <span className="grid h-9 w-9 flex-none place-items-center rounded-lg border border-[#bfe0ef] bg-card text-[#2779a7] dark:border-[#2779a7]/50 dark:text-[#7cc3e6]">
                    <CalendarCheck className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1e5f83] dark:text-[#cfe6f2]">{load.rollingLink.title}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-semibold uppercase tracking-wide text-[#2779a7] dark:text-[#7cc3e6]">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" /> {load.rollingLink.slot_minutes} min
                      </span>
                      {load.rollingLink.location && (
                        <span className="inline-flex items-center gap-1 normal-case">
                          <MapPin className="h-3.5 w-3.5" /> {load.rollingLink.location}
                        </span>
                      )}
                    </p>
                    {load.rollingLink.description && (
                      <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                        {load.rollingLink.description}
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* The slot picker keeps its top margin only when something sits above it. */}
            <div className={embed.hideDetails ? undefined : "mt-6"}>
              {load.rollingLink.slots.length === 0 ? (
                <p className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200">
                  No open times right now. Check back soon.
                </p>
              ) : showForm ? (
                <div className="space-y-4">
                  <SelectionTray
                    selected={selected}
                    onRemove={removeSelected}
                    onBack={() => setShowForm(false)}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="cl-name">Your name</Label>
                    <Input id="cl-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="cl-email">Email</Label>
                    <Input
                      id="cl-email"
                      type="email"
                      value={email}
                      maxLength={254}
                      placeholder="you@example.com"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="cl-note">Anything to add? (optional)</Label>
                    <textarea
                      id="cl-note"
                      value={note}
                      maxLength={500}
                      rows={2}
                      onChange={(e) => setNote(e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                    <p
                      className={cn(
                        "text-right text-xs",
                        note.length > 450 ? "text-rose-600" : "text-muted-foreground",
                      )}
                    >
                      {note.length}/500
                    </p>
                  </div>
                  {/* Honeypot — real humans leave this blank. */}
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    className="hidden"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                  {error && (
                    <p className="rounded-xl border border-rose-300 bg-rose-50 p-2 text-sm text-rose-700 dark:border-rose-800/60 dark:bg-rose-950/30 dark:text-rose-200">
                      {error}
                    </p>
                  )}
                  <Button
                    className="w-full bg-[#df6c4f] text-white hover:bg-[#c85b40]"
                    onClick={submit}
                    disabled={booking || !email.trim()}
                  >
                    {booking ? "Booking…" : "Confirm booking"}
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="mb-3 text-sm font-bold text-foreground">Available times</h3>
                  <BookingCalendar
                    slots={openSlots}
                    slotMinutes={load.rollingLink.slot_minutes}
                    onPick={pick}
                    allowTimezoneChange
                    viewMonth={paging.month}
                    onMonthChange={paging.goToMonth}
                    canGoPrev={canGoPrev(paging.month, bounds)}
                    canGoNext={canGoNext(paging.month, bounds)}
                    loadingMonth={paging.loading}
                  />
                </>
              )}
            </div>
          </Card>
        </>
      )}

      <PublicFooter />
    </PublicPageShell>
  );
}

/** The chosen-slots tray shown while multi-booking or on the form. */
function SelectionTray({
  selected,
  onRemove,
  onBack,
}: {
  selected: PublicRollingLinkSlot[];
  onRemove: (start: string) => void;
  onBack?: () => void;
}) {
  return (
    <div className="rounded-xl border border-[#bfe0ef] bg-[#e9f3f7] p-3 dark:border-[#2779a7]/50 dark:bg-[#1e5f83]/20">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wide text-[#1e5f83] dark:text-[#cfe6f2]">
          {selected.length === 1 ? "Your slot" : `Your ${selected.length} slots`}
        </p>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="text-xs font-semibold text-[#2779a7] hover:underline dark:text-[#7cc3e6]"
          >
            change
          </button>
        )}
      </div>
      <ul className="space-y-1">
        {selected.map((s) => (
          <li key={s.start} className="flex items-center justify-between gap-2 text-sm">
            <span className="truncate text-foreground">
              {fmtDay(s.start)} · {fmtTime(s.start)}
            </span>
            <button
              type="button"
              aria-label="Remove slot"
              onClick={() => onRemove(s.start)}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-[#bfe0ef] text-[#2779a7] transition hover:bg-[#d6e9f2] dark:border-[#2779a7]/50 dark:text-[#7cc3e6] dark:hover:bg-[#1e5f83]/30"
            >
              <X className="h-3 w-3" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
